import type { CategoryDatum, PatientMixReport } from '#shared/types';
import { BarChart, LineChart, type Series } from '#client/components/charts';
import { Card } from '#client/components/ui/Card';
import { BreakdownChartCard, BreakdownTableCard } from './BreakdownCards';
import { SectionShell } from './SectionShell';
import { formatterFor } from './format';
import { comparisonLabel, dimensionLabel, isSeriesEmpty, sumValues } from './report.logic';
import { toBarSeries, toCompareSeries, withComparison } from './to-series';
import { useAnalyticsFilters } from './useAnalyticsFilters';
import { useAnalyticsSection } from './useAnalyticsSection';

/**
 * Who the lab saw, and who still owes it money.
 *
 * Visit-grained throughout, which is the grain the money is recorded at — so
 * nothing here is apportioned. The one figure that is not exact is the count of
 * *people*, and the payload carries the reason.
 */
export function PatientMixPage() {
  const filters = useAnalyticsFilters('patients');
  const { data, loading, error } = useAnalyticsSection<PatientMixReport>('patients', filters);

  return (
    <SectionShell
      section="patients"
      filters={filters}
      data={data}
      loading={loading}
      error={error}
      metrics={data?.headline}
      empty={data !== undefined && isSeriesEmpty(data.series)}
    >
      {data && <PatientMixGrid report={data} />}
    </SectionShell>
  );
}

function PatientMixGrid({ report }: { report: PatientMixReport }) {
  const compare = toCompareSeries(report.series, comparisonLabel(report.compare));
  const label = dimensionLabel(report.by);

  return (
    <div className="c-an__grid">
      <VisitsCard series={report.series} compare={compare} />
      <BreakdownChartCard
        label={label}
        breakdown={report.breakdown}
        centre={{
          value: String(report.returning.new + report.returning.returning),
          label: 'visits',
        }}
        seriesLabel="Visits"
      />
      <ReceivablesCard receivables={report.receivables} />
      <BreakdownTableCard
        label={label}
        breakdown={report.breakdown}
        countHeader="Visits"
        secondaryHeader="Billed"
      />
    </div>
  );
}

function VisitsCard({
  series,
  compare,
}: {
  series: PatientMixReport['series'];
  compare: Series | null;
}) {
  return (
    <Card className="c-an__cell c-an__cell--wide" title="Visits over time">
      <LineChart
        title="Visits registered"
        series={withComparison(series, compare)}
        area={compare === null}
        format={formatterFor('count')}
        height={260}
      />
    </Card>
  );
}

function ReceivablesCard({ receivables }: { receivables: readonly CategoryDatum[] }) {
  return (
    <Card className="c-an__cell" title="Money still owed" subtitle="By how long ago the visit was">
      {sumValues(receivables) === 0 ? (
        <p className="c-an__caption">Every visit in this period is settled.</p>
      ) : (
        <BarChart
          title="Open balances by age of visit"
          series={[toBarSeries('count', 'Visits owing', receivables)]}
          format={formatterFor('count')}
          height={200}
          categoryLabel="Age"
        />
      )}
    </Card>
  );
}
